/**
 * Browser Notification Utility
 * Handles requesting permission, push subscription, and native browser notifications.
 */

import { getApiBaseUrl } from '../config/api.config';
import { registerWebPushSubscription } from '../services/push.service';

const NOTIFICATION_STORAGE_KEY = 'tropayment_notif_permission_asked';
const LAST_NOTIF_IDS_KEY = 'tropayment_last_notif_ids';

export function isNotificationSupported(): boolean {
  return 'Notification' in window;
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!isNotificationSupported()) return 'unsupported';

  const permission = await Notification.requestPermission();
  localStorage.setItem(NOTIFICATION_STORAGE_KEY, 'true');
  return permission;
}

/** Check if Push API is supported (service worker + push manager) */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Subscribe via pushManager + VAPID and optionally run a custom backend callback.
 * Prefer `registerWebPushSubscription()` for the standard flow.
 */
export async function subscribeToPush(sendToBackend?: (sub: PushSubscriptionJSON) => Promise<void>): Promise<boolean> {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;

  try {
    if (sendToBackend) {
      const reg = await navigator.serviceWorker.ready;
      const vapidRes = await fetch(`${getApiBaseUrl()}/v1/push/vapid-public`);
      const { vapid_public_key } = (await vapidRes.json()) as { vapid_public_key?: string | null };
      if (!vapid_public_key) return false;

      const padding = '='.repeat((4 - (vapid_public_key.length % 4)) % 4);
      const b64 = (vapid_public_key + padding).replace(/-/g, '+').replace(/_/g, '/');
      const raw = atob(b64);
      const key = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) key[i] = raw.charCodeAt(i);

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key as BufferSource,
      });
      const json = sub.toJSON();
      if (json.endpoint && json.keys?.p256dh && json.keys?.auth) {
        await sendToBackend(json);
        return true;
      }
      return false;
    }
    return registerWebPushSubscription();
  } catch {
    return false;
  }
}

/** Legacy helper — delegates to push.service */
export async function syncPushSubscriptionWithBackend(): Promise<boolean> {
  return registerWebPushSubscription();
}

export function hasAskedPermission(): boolean {
  return localStorage.getItem(NOTIFICATION_STORAGE_KEY) === 'true';
}

/** True when the tab/PWA window is in the foreground (polling fallback only). */
export function isAppInForeground(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible';
}

/**
 * Foreground-only fallback: forwards to SW via postMessage.
 * Background delivery must use server Web Push → service worker `push` event.
 */
export async function showForegroundNotificationFallback(
  title: string,
  options?: {
    body?: string;
    icon?: string;
    tag?: string;
    data?: Record<string, unknown>;
    onClick?: string;
  }
): Promise<void> {
  if (!isNotificationSupported() || Notification.permission !== 'granted') return;
  if (!isAppInForeground()) return;
  if (!('serviceWorker' in navigator)) return;

  const payload = {
    title,
    body: options?.body,
    message: options?.body,
    icon: options?.icon || '/icons/app-icon.png',
    tag: options?.tag,
    url: options?.onClick,
    type: (options?.data as { type?: string } | undefined)?.type,
    requireInteraction: true,
    data: options?.data,
  };

  try {
    const registration = await navigator.serviceWorker.ready;
    const worker = navigator.serviceWorker.controller ?? registration.active;
    if (worker) {
      worker.postMessage({ type: 'SHOW_NOTIFICATION', payload });
      return;
    }
    await registration.showNotification(title, {
      body: options?.body,
      icon: options?.icon || '/icons/app-icon.png',
      badge: '/icons/icon-192.png',
      tag: options?.tag,
      data: { url: options?.onClick },
      requireInteraction: true,
    });
  } catch {
    // SW not ready — skip rather than falling back to page Notification API
  }
}

/** @deprecated Use showForegroundNotificationFallback — kept for existing imports */
export const showBrowserNotification = showForegroundNotificationFallback;

/** Handle notification clicks routed from the service worker (SPA navigate). */
export function setupServiceWorkerNotificationRouting(
  onNavigate: (url: string) => void
): () => void {
  if (!('serviceWorker' in navigator)) return () => {};

  const handler = (event: MessageEvent) => {
    if (event.data?.type === 'NOTIFICATION_CLICK' && typeof event.data.url === 'string') {
      onNavigate(event.data.url);
    }
  };

  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}

/**
 * Track which notification IDs we've already shown browser notifications for,
 * so we don't duplicate them.
 */
export function getShownNotificationIds(): Set<string> {
  try {
    const stored = localStorage.getItem(LAST_NOTIF_IDS_KEY);
    return new Set(stored ? JSON.parse(stored) : []);
  } catch {
    return new Set();
  }
}

export function markNotificationShown(id: string): void {
  const ids = getShownNotificationIds();
  ids.add(id);
  // Keep only last 100 IDs
  const arr = Array.from(ids).slice(-100);
  localStorage.setItem(LAST_NOTIF_IDS_KEY, JSON.stringify(arr));
}

/**
 * Get the appropriate notification click URL based on notification type.
 */
export function getNotificationUrl(type: string, data?: Record<string, any>): string {
  const base = '/dashboard';
  switch (type) {
    case 'payment_confirmed':
    case 'new_invoice':
      return `${base}/invoices`;
    case 'deposit_received':
      return `${base}/wallet`;
    case 'withdrawal_requested':
    case 'withdrawal_approved':
    case 'withdrawal_rejected':
    case 'settlement_completed':
      return `${base}/withdrawals`;
    case 'kyc_approved':
    case 'kyc_rejected':
    case 'kyc_submitted':
      return `${base}/kyc`;
    case 'ticket_reply':
    case 'new_ticket':
    case 'ticket_closed':
    case 'ticket_created':
      return data?.ticket_id ? `${base}/tickets` : `${base}/tickets`;
    case 'chat_reply':
      return base;
    case 'new_device_login':
    case 'password_reset':
      return '/dashboard/settings';
    case 'api_key_deactivated':
    case 'api_key_created':
      return '/dashboard/api-keys';
    case 'wallet_created':
      return '/dashboard/wallet';
    case 'plan_upgraded':
      return '/dashboard/settings';
    case 'account_suspended':
    case 'account_activated':
    case 'account_deactivated':
      return '/dashboard';
    default:
      return '/dashboard';
  }
}
